import React, { useRef, useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fromJS } from "immutable";
import { updateProjectData, updateTimelineTime } from "../../../../redux/actions/timelineUtils";
import { buildLocalSmartfocus } from "../../../timeline/timeline-helper";
import { setIsZoomLock, setSelectedItems, setSelectedZoomId } from "../../../../redux/actions/appUtils";
import { CursorContainer, CursorTitleContainer, SmartfocusContainer, TitleContainer } from "./smartfocus-components";
import Typography from "../../../../common-components/Typography";
import content from "../../../../constants/content";
import vmTheme from "../../../../constants/theme";
import { font } from "../../../../constants/font";
import Toggle from "../../../../common-components/Toggle";
import { STATIC_PATH } from "../../../../constants/config";
import { Scroll } from "../../panel-components";
import usePanelScrollHeight from "../../../../helper/hooks/useScrollHeight";
import usePreventScrollOnArrowKeys from "../../../../helper/hooks/usePreventScrollOnArrowKeys";
import useMobileDetection from "../../../../helper/hooks/useMobileDetection";
import SmartfocusWorkspace from "./smartfocus-workspace";
import SmartfocusScale from "./smartfocus-scale";
import AddZoomComponent from "./smartfocus-addzoom";
import Spinner, { SPINNER_TYPE } from "../../../../common-components/Spinner";
import Action from "../../../../common-components/Action";
import { findMatchingZoomOut } from "../../../../helper/smartfocusHelper";

const SmartfocusPanel = () => {
    const theme = useSelector((state) => state.app.get("theme"));
    const panelRef = useRef(null);
    const isMobile = useMobileDetection();
    const [showLoader, setLoader] = useState(true);
    const [cursorLock, setSetCursorLock] = useState(false);
    const [scaleValue, setScaleValue] = useState(70);
    const [addZoom, setAddZoom] = useState(false);
    const workspaceStage = useSelector((state) => state.app.get("workspaceStage"));
    const isZoomLock = useSelector((state) => state.app.get("isZoomLock"));
    const selectedWorkspaceItem = useSelector((state) => state.app.get("selectedItems"));
    const playhead = useSelector((state) => state.app.get("playhead"));
    const project = useSelector((state) => state.projectDetails);
    const pWidth = project.get("width");
    const pHeight = project.get("height");
    const selectedValue = useSelector((state) => state.app.get("selectedSubtitles"));
    const selectedZoomId = useSelector((state) => state.app.get("selectedZoomId"));
    const selectedItem = selectedValue.size > 0 ? selectedValue?.get(0)?.split("-")[0] : selectedWorkspaceItem.get(0);
    const item = useSelector((state) => {
        return state.projectDetails.getIn([
            "workspaceItems",
            selectedItem
        ]);
    });
    const id = selectedZoomId.split("-")[0]
    const selectedZoomItem = useSelector((state) => {
        return state.projectDetails.getIn([
            "workspaceItems",
            id
        ]);
    });
    const mainScrollableHeight = usePanelScrollHeight({
        panelRef,
        workspaceStage,
        shouldUpdate: true,
    });

    const isRecording = false;
    const preventScrollOnArrowKeys = usePreventScrollOnArrowKeys();
    const dispatch = useDispatch();
    const heightDimension = isRecording || (pHeight > 450 && pWidth > pHeight ) ? 210 :  195;

    const handleDeleteZoom = useCallback(() => {
        if (!selectedZoomId || !item || isZoomLock) return;
        const zoomId = selectedZoomId.substring(selectedZoomId.indexOf("-") + 1);
        const zoomData = selectedZoomItem.get("zoomData")?.toJS() || {};
        const workspaceId = id;
        const beforeElementId = findMatchingZoomOut(zoomData, zoomData[zoomId]?.zoomIn?.start);
        // Create a new zoom data object without the deleted zoom
        const updatedZoomData = { ...zoomData };

        delete updatedZoomData[zoomId];

        if (beforeElementId) {
            updatedZoomData[beforeElementId].zoomOut.disableZoomOut = false;
        }

        // Prepare the update object for updateTimelineTime
        const toUpdate = [
            {
                id: workspaceId,
                container: "workspaceItems",
                toUpdate: {
                    zoomData: updatedZoomData
                }
            }
        ];

        // Update the timeline with the new zoom data
        dispatch(updateTimelineTime({ toUpdate }));

        // Update the local zoom data
        const updatedProject = project.setIn(
            ["workspaceItems", workspaceId, "zoomData"],
            fromJS(updatedZoomData)
        );

        const updatedLocalZoom = buildLocalSmartfocus(updatedProject);
        const updatedProjects = updatedLocalZoom.getIn(["localZoom"]);

        // Update the project data with the new local zoom
        dispatch(
            updateProjectData({
                localZoom: updatedProjects,
            })
        );

        // Get the first non-deleted zoom data
        const firstNonDeletedZoom = Object.entries(updatedZoomData).find(([, data]) =>
            !data._deleted
        );

        if (firstNonDeletedZoom) {
            const [firstZoomId] = firstNonDeletedZoom;
            dispatch(setSelectedItems({
                propWindowType: "SMART_FOCUS",
                selectedSubtitles: [`${workspaceId}-${firstZoomId}`]
            }));
            dispatch(setSelectedZoomId(firstZoomId));
        } else {
            dispatch(setSelectedItems([]));
            dispatch(setSelectedZoomId(""));
        }
        setAddZoom(true);
    }, [selectedValue, item, selectedItem, dispatch, project])

    useEffect(() => {
        if (!item || playhead  === undefined || playhead < item.get("enterStart")) {
            setAddZoom(true);
            return;
        }
        const zoomData = item.get("zoomData");
        const enterStart = item.get("enterStart") || 0;
        const videoStart = item.get("videoStart") || 0;
        const itemEnter = enterStart - videoStart;
        if (!zoomData || zoomData.isEmpty()) {
            setAddZoom(true);
            return;
        }

        const zoomGroups = Object.values(zoomData.toJS());
        const isInZoom = zoomGroups.some((zoomGroup) => {
            let zoomInStart = null;

            // Sort zoom stages by their start time
            const sortedStages = Object.values(zoomGroup).sort((a, b) => a.start - b.start);

            return sortedStages.some((zoomStage) => {
                if (zoomStage.type === "zoomIn") {
                    zoomInStart = zoomStage.start + itemEnter;
                    const scaleValue = (zoomStage.width / pWidth) * 100;
                    setScaleValue(scaleValue)
                }
                return (
                    zoomInStart !== null &&
                    zoomStage.type === "zoomOut" &&
                    playhead >= zoomInStart &&
                    playhead <= (zoomStage.end + itemEnter)
                );
            });
        });
        setAddZoom(!isInZoom);
    }, [playhead, item]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            // Check for Delete or Backspace key and make sure we have a selection
            if ((e.key === 'Delete') || (e.key === 'Backspace') && selectedValue?.size > 0) {
                e.preventDefault();
                e.stopPropagation();
                handleDeleteZoom();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [selectedValue, handleDeleteZoom]);

    useEffect(() => {
        const zoomData = item?.get("zoomData");
        if (!zoomData || zoomData.size === 0) {
            setLoader(false);
            setAddZoom(true);
        } else {
            setLoader(false);
        }
        setSetCursorLock(isRecording);
    }, [isRecording]);

    useEffect(() => {
        dispatch(setIsZoomLock(cursorLock));
    }, [cursorLock]);

    return (
        <Scroll
            height={isMobile ? "330px" : mainScrollableHeight}
            ref={panelRef}
            onKeyDown={preventScrollOnArrowKeys}
            tabIndex={0}
            style={{ paddingBottom: "2rem" }}
        >
            {
                showLoader &&
                <Spinner
                    type={SPINNER_TYPE.RING_SPINNER}
                    spinnerHeight={"130px"}
                />
            }
            {!showLoader && addZoom && (<AddZoomComponent
                text={
                    item?.get("zoomData")?.size > 0
                        ? content.NO_ZOOM_SELECTED
                        : "Smart Focus"
                }
                subText={
                    item?.get("zoomData")?.size > 0
                        ? content.SELECT_ZOOM_TIMELINE
                        : content.EYE_CATCHING_ZOOM_DESCRIPTION
                }
            />)}
            {
                !addZoom && !showLoader && (
                    <SmartfocusContainer style={{ padding: "17px 17px 17px 0px" }}>
                        <CursorContainer>
                            {
                                isRecording ? (<CursorTitleContainer>
                                    <TitleContainer>
                                        <img src={`${STATIC_PATH}${"cursor-track.svg"}`} alt="" />
                                        <Typography
                                            content={content.CURSOR_TRACKING}
                                            margin="0px 0px 0px 17px !important"
                                            color={vmTheme[theme].panelPrimaryColor}
                                            font={font.boldBase}
                                            height="19px"
                                            padding={"2px 0px"}
                                            style={{ lineHeight: "19px" }}
                                        />
                                    </TitleContainer>
                                    <Toggle
                                        toggleColor={vmTheme[theme].checkBoxTickColor}
                                        value={cursorLock}
                                        width={"30px"}
                                        height={"16px"}
                                        afterHeight={"14px"}
                                        afterWidth={"14px"}
                                        afterLeft={cursorLock ? "5px" : "1px"}
                                        afterBottom={"1.2px"}
                                        clickHandler={() => setSetCursorLock(prev => !prev)}
                                    />
                                </CursorTitleContainer>) : (
                                    <div style={{ display: "flex", flexDirection: "column" }}>
                                        <Typography
                                            content={"Target"}
                                            color={vmTheme[theme].panelPrimaryColor}
                                            font={font.mediumMini}
                                            height="19px"
                                            padding={"2px 0px"}
                                        />
                                        <Typography
                                            content={"Move the circle handle to target the zoom"}
                                            color={vmTheme[theme].labelSubtextColor}
                                            font={font.normalBase_15}
                                            height="19px"
                                            padding={"2px 0px"}
                                        />
                                    </div>
                                )
                            }
                            <div style={{
                                background: "black", display: "flex", width: 292, height: heightDimension,
                                justifyContent: "center", marginTop: "8px", borderRadius: "12px"
                            }}>
                                <SmartfocusWorkspace isOpen={true} scaleValue={scaleValue} isLock={cursorLock} />
                            </div>
                        </CursorContainer>
                        <SmartfocusScale handleScaleValue={setScaleValue} isLock={cursorLock} />
                        <Action
                            background={vmTheme[theme].polarColor}
                            borderRadius="8px"
                            font={font.normalBase_medium_16}
                            fontColor={vmTheme[theme].tabTextColor}
                            width="316px"
                            height="32px"
                            margin="16px 0px 8px"
                            onClick={handleDeleteZoom}
                            text={"Delete"}
                            borderWidth={`1px`}
                            borderColor={vmTheme[theme].searchBorder}
                        />
                    </SmartfocusContainer>
                )
            }
        </Scroll>
    );
};

export default SmartfocusPanel;
